import { parseUtcDate } from '@/lib/utils';
import type { PendingActionDto, SentinelAiEventLogWireDto, SentinelConversation, WorkspaceMessage } from './types';

export function buildThreadMessages(
  conversation: SentinelConversation | null,
  sentReply: string | null,
  privateNotes: WorkspaceMessage[],
): WorkspaceMessage[] {
  if (!conversation) return [];

  const conversationHasReply =
    sentReply !== null &&
    conversation.messages.some(
      (message) => message.from === 'agent' && !message.isPrivate && message.markdown.trim() === sentReply.trim(),
    );
  const persistedNoteBodies = new Set(
    conversation.messages.filter((message) => message.isPrivate).map((message) => message.markdown.trim()),
  );
  const optimisticNotes = privateNotes.filter((note) => !persistedNoteBodies.has(note.markdown.trim()));

  return [
    ...conversation.messages,
    ...optimisticNotes,
    ...(sentReply && !conversationHasReply
      ? [
          {
            id: -1,
            markdown: sentReply,
            from: 'agent' as const,
            senderName: 'Kim AI',
            createdAt: new Date().toISOString(),
          },
        ]
      : []),
  ];
}

/** Indents bullets a reply left flush under a numbered item, so the list stays one list. */
export function nestBulletsUnderNumbers(markdown: string): string {
  let indent = '';
  let inCodeBlock = false;
  return markdown
    .split('\n')
    .map((line) => {
      if (line.trimStart().startsWith('```')) {
        inCodeBlock = !inCodeBlock;
        indent = '';
        return line;
      }
      if (inCodeBlock || line.trim() === '') return line;
      // Replies number their items both ways: "1." and "1)".
      const marker = /^\d+[.)]\s+/.exec(line);
      if (marker) {
        // Bullets nest only when they clear the marker, which "10." makes wider.
        indent = ' '.repeat(marker[0].length);
        return line;
      }
      if (indent && /^[-*+]\s/.test(line)) return `${indent}${line}`;
      // Anything else at the margin closes the item, and its own bullets stay flush.
      if (!/^\s/.test(line)) indent = '';
      return line;
    })
    .join('\n');
}

/** "Jul 2, 9:06 AM" — the stamp every bubble in the workspace carries. */
export function timeLabel(iso: string): string {
  const date = parseUtcDate(iso);
  return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

/**
 * The turn whose actions the card offers: the ticket's latest reply, if it has any.
 *
 * A newer reply (a refine, a follow-up) replaces an older turn's actions even when it carries none
 * of its own, so only the latest turn with a reply or actions counts; the backend refuses to run
 * any other. Ordered by id, so the pick does not depend on the order the turns arrive in.
 */
export function latestPendingActionsTurn(turns: SentinelAiEventLogWireDto[]): SentinelAiEventLogWireDto | undefined {
  const latest = [...turns]
    .sort((a, b) => b.id - a.id)
    .find((turn) => turn.response_message || turn.pending_actions?.plan?.actions?.length);
  return latest?.pending_actions?.plan?.actions?.length ? latest : undefined;
}

const MAX_NAMED_VIEWERS = 2;

/**
 * What the presence banner says: up to two people by name, the rest as a count.
 * "Asha is typing…" wins over "Asha and Ravi are also viewing this ticket" — who is about to
 * reply matters more.
 */
export function presenceLabel<T extends { name: string; typing: boolean }>(
  viewers: T[],
): { named: T[]; others: number; what: string } | null {
  const typing = viewers.filter((viewer) => viewer.typing);
  const shown = typing.length > 0 ? typing : viewers;
  if (shown.length === 0) return null;
  const verb = shown.length === 1 ? 'is' : 'are';
  return {
    named: shown.slice(0, MAX_NAMED_VIEWERS),
    others: Math.max(0, shown.length - MAX_NAMED_VIEWERS),
    what: typing.length > 0 ? `${verb} typing…` : `${verb} also viewing this ticket`,
  };
}

/** A status change in the words the author picked it by in the editor — the tool name is plumbing. */
const STATUS_LABELS: Record<string, string> = {
  open: 'Re-open ticket',
  pending: 'Mark as pending',
  snoozed: 'Snooze ticket',
  closed: 'Close ticket',
};

/** What the action will do, as the agent should read it: "Add tag · refund-done", "Mark as pending". */
export function describePendingAction(action: PendingActionDto): { label: string; value: string | null } {
  // An action that takes no arguments can arrive with no `inputs` at all: stored empty objects are dropped.
  const inputs = action.inputs ?? {};
  const status = inputs['status'];
  if (action.tool === 'tickets_status_set' && typeof status === 'string' && STATUS_LABELS[status]) {
    return { label: STATUS_LABELS[status]!, value: null };
  }
  if (action.tool === 'tickets_close') return { label: 'Close ticket', value: null };
  // A follow-up wait's inputs are its whole program: what it means to the agent is one line.
  if (action.tool === 'followup_wait') return { label: 'Follow up again later', value: null };
  const label =
    action.tool === 'tickets_tag_add'
      ? 'Add tag'
      : action.tool === 'tickets_status_set'
        ? 'Change status'
        : (action.label ?? action.tool.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()));
  const values = Object.entries(inputs)
    .filter(([key]) => key !== 'ticketId')
    .flatMap(([, value]) => (Array.isArray(value) ? value.map(String) : value == null ? [] : [String(value)]));
  return { label, value: values.length > 0 ? values.join(', ') : null };
}
